import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  limit,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import HeaderBar from "../../components/HeaderBar";
import MenuBar from "../../components/MenuBar";
import { calculatePoints, cleanPhone } from "../../utils/customerEntryPoints";
import { customerProfileDocId, LOYALTY_TIERS, formatCustomerEntryActorLabel, resolveCustomerEntryEnteredBy } from "../../utils/customerEntryProfile";
import { writeAuditLog } from "../../utils/auditLog";

function isAdminRole() {
  try {
    const r = JSON.parse(localStorage.getItem("user") || "{}")?.role;
    return r === "admin" || r === "administrator";
  } catch {
    return false;
  }
}

function canEditEntryRole() {
  try {
    const r = JSON.parse(localStorage.getItem("user") || "{}")?.role;
    return r === "admin" || r === "administrator" || r === "staff";
  } catch {
    return false;
  }
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";

export default function CustomerEntryDetailPage() {
  const { customerName: encoded } = useParams();
  const customerName = encoded ? decodeURIComponent(encoded) : "";
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin] = useState(() => isAdminRole());
  const [canEdit] = useState(() => canEditEntryRole());
  const [editOpen, setEditOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [profile, setProfile] = useState({
    notes: "",
    loyaltyTier: "—",
    birthday: "",
    anniversary: "",
  });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  useEffect(() => {
    if (!customerName) return undefined;
    const qy = query(collection(db, "customer_entries"), where("customerName", "==", customerName));
    return onSnapshot(qy, (snap) => {
      const list = [...snap.docs].sort((a, b) => {
        const ta = a.data()?.createdAt?.toDate?.()?.getTime?.() ?? 0;
        const tb = b.data()?.createdAt?.toDate?.()?.getTime?.() ?? 0;
        return tb - ta;
      });
      setDocs(list);
      setLoading(false);
    });
  }, [customerName]);

  const profileId = customerName ? customerProfileDocId(customerName) : "";

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "customer_profiles", profileId));
        if (cancelled || !snap.exists) return;
        const d = snap.data() || {};
        setProfile({
          notes: d.notes?.toString() ?? "",
          loyaltyTier: d.loyaltyTier?.toString() || "—",
          birthday: d.birthday?.toString() ?? "",
          anniversary: d.anniversary?.toString() ?? "",
        });
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const lastVisit = useMemo(() => {
    let max = 0;
    for (const d of docs) {
      if (d.data()?.voided === true) continue;
      const t = d.data()?.createdAt?.toDate?.()?.getTime?.() ?? 0;
      if (t > max) max = t;
    }
    return max ? new Date(max) : null;
  }, [docs]);

  const saveProfile = async () => {
    if (!profileId) return;
    const auth = getAuth();
    setSavingProfile(true);
    try {
      await setDoc(
        doc(db, "customer_profiles", profileId),
        {
          customerName,
          notes: profile.notes.trim(),
          loyaltyTier: profile.loyaltyTier === "—" ? "" : profile.loyaltyTier,
          birthday: profile.birthday.trim(),
          anniversary: profile.anniversary.trim(),
          updatedAt: serverTimestamp(),
          updatedByEmail: auth.currentUser?.email ?? null,
          updatedByUid: auth.currentUser?.uid ?? null,
        },
        { merge: true }
      );
      toast.success("Profile saved");
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setSavingProfile(false);
    }
  };

  const totals = useMemo(() => {
    let totalAmount = 0;
    let totalPoints = 0;
    let phone;
    docs.forEach((d) => {
      const data = d.data() || {};
      if (data.voided === true) return;
      phone ??= data.phone?.toString();
      const amount = Number(data.amount) || 0;
      totalAmount += amount;
      totalPoints += data.points != null ? Number(data.points) : calculatePoints(amount);
    });
    return { totalAmount, totalPoints, phone };
  }, [docs]);

  const copyFollowUp = async () => {
    const phone = totals.phone?.toString() ?? "";
    const msg = `Hi ${customerName},\nThank you for shopping with us. We value your loyalty (${profile.loyaltyTier && profile.loyaltyTier !== "—" ? `${profile.loyaltyTier} tier` : "rewards"}).\nLet us know if you need anything.`;
    try {
      await navigator.clipboard.writeText(`${phone}\n\n${msg}`);
      toast.success("Phone and message copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const openWhatsApp = () => {
    const phone = totals.phone?.toString() ?? "";
    const digits = cleanPhone(phone).replace(/^\+/, "");
    if (!digits) {
      toast.error("No phone number");
      return;
    }
    const msg = `Hi ${customerName}, thank you for your continued support!`;
    window.open(`https://wa.me/${digits.replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const openEdit = (d) => {
    setEditDoc(d);
    setEditOpen(true);
  };

  const mergeInto = async () => {
    const entries = docs;
    if (!entries.length) return;
    const others = new Set();
    try {
      const all = await getDocs(collection(db, "customer_entries"));
      all.docs.forEach((x) => {
        const n = (x.data()?.customerName ?? "").toString().trim();
        if (n && n !== customerName) others.add(n);
      });
    } catch (e) {
      toast.error(String(e?.message || e));
      return;
    }
    const sorted = [...others].sort();
    if (!sorted.length) {
      toast.error("No other customer to merge into.");
      return;
    }
    const target = window.prompt(
      `Merge all entries from "${customerName}" into customer name:\n(${sorted.slice(0, 20).join(", ")}${sorted.length > 20 ? "…" : ""})\n\nEnter exact target name:`
    );
    if (!target || !target.trim()) return;
    if (!sorted.includes(target.trim())) {
      toast.error("Target name must match an existing customer.");
      return;
    }
    const targetName = target.trim();
    if (!window.confirm(`Merge ${entries.length} entries into "${targetName}"?`)) return;

    let targetPhone = "";
    try {
      const one = await getDocs(
        query(collection(db, "customer_entries"), where("customerName", "==", targetName), limit(1))
      );
      if (!one.empty) targetPhone = one.docs[0].data()?.phone?.toString() ?? "";
    } catch {
      /* ignore */
    }

    try {
      const batch = writeBatch(db);
      entries.forEach((x) => {
        batch.update(x.ref, { customerName: targetName, phone: targetPhone });
      });
      await batch.commit();
      toast.success(`Merged into ${targetName}`);
      navigate("/reports/customer-entry");
    } catch (e) {
      toast.error(String(e?.message || e));
    }
  };

  const sendSms = async (d) => {
    const data = d.data() || {};
    const phone = data.phone?.toString() ?? "";
    if (!phone || phone === "-") {
      toast.error("Phone not available");
      return;
    }
    const inv =
      Array.isArray(data.invoiceNumbers) && data.invoiceNumbers.length
        ? data.invoiceNumbers.join(", ")
        : data.invoiceNo?.toString() ?? "-";
    const amount = Number(data.amount) || 0;
    const pts = data.points != null ? Number(data.points) : calculatePoints(amount);
    const message = `Dear ${customerName},\n\nInvoice: ${inv}\nAmount: ₹${Math.round(amount)}\nPoints: ${Math.round(pts)}\n\nThank you for your purchase!`;
    const c = cleanPhone(phone);
    window.location.href = `sms:${c}?body=${encodeURIComponent(message)}`;
    try {
      await updateDoc(doc(db, "customer_entries", d.id), {
        messageSent: true,
        messageSentAt: serverTimestamp(),
        messageText: message,
      });
      toast.success("SMS app opened");
    } catch (e) {
      console.warn(e);
    }
  };

  if (!customerName) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-gray-100 text-gray-900">
        <MenuBar />
        <HeaderBar />
        <div className="p-6 text-center">
          <p className="text-gray-600">Invalid customer</p>
          <button
            type="button"
            className="mt-3 text-indigo-600 font-medium hover:underline"
            onClick={() => navigate("/reports/customer-entry")}
          >
            Back to Customer entry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-gray-50 to-slate-100 pb-12">
      <MenuBar />
      <HeaderBar />
      <header className="sticky top-0 z-10 border-b border-gray-200/80 bg-white/95 px-4 py-3 shadow-sm backdrop-blur backdrop-saturate-150">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/reports/customer-entry")}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            ← Back
          </button>
          <h1 className="flex-1 truncate text-base font-semibold text-gray-900">{customerName}</h1>
          {isAdmin && (
            <button
              type="button"
              onClick={mergeInto}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              Merge
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pt-5">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-9 w-9 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          </div>
        ) : docs.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-gray-200 bg-white py-12 text-center text-sm text-gray-500">
            No entries found
          </p>
        ) : (
          <>
            <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-md shadow-gray-200/50">
              {totals.phone && totals.phone !== "-" && (
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-lg" aria-hidden>
                      📞
                    </span>
                    {totals.phone}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={copyFollowUp}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-100"
                    >
                      Copy phone + message
                    </button>
                    <button
                      type="button"
                      onClick={openWhatsApp}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
                    >
                      WhatsApp
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Entries</p>
                  <p className="mt-0.5 text-xl font-bold text-indigo-600">
                    {docs.filter((d) => d.data()?.voided !== true).length}
                  </p>
                </div>
                <div className="border-x border-gray-100">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Amount</p>
                  <p className="mt-0.5 text-xl font-bold text-emerald-600">₹{totals.totalAmount.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Points</p>
                  <p className="mt-0.5 text-xl font-bold text-indigo-600">{Math.round(totals.totalPoints)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-indigo-100 bg-white p-5 shadow-md shadow-gray-200/50">
              <h2 className="text-sm font-semibold text-gray-900">Customer profile</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Notes, loyalty, and reminders (stored separately).{" "}
                {lastVisit && (
                  <>
                    Last visit:{" "}
                    <span className="font-medium text-gray-700">
                      {lastVisit.toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </>
                )}
              </p>
              <div className="mt-3 space-y-3 text-sm">
                <div>
                  <label className="text-xs font-medium text-gray-600">Notes</label>
                  <textarea
                    className={`${inputClass} mt-1 min-h-[72px]`}
                    value={profile.notes}
                    onChange={(e) => setProfile((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Preferences, follow-ups…"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Loyalty tier</label>
                    <select
                      className={`${inputClass} mt-1`}
                      value={profile.loyaltyTier}
                      onChange={(e) => setProfile((p) => ({ ...p, loyaltyTier: e.target.value }))}
                    >
                      {LOYALTY_TIERS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Birthday (optional)</label>
                    <input
                      type="date"
                      className={`${inputClass} mt-1`}
                      value={profile.birthday}
                      onChange={(e) => setProfile((p) => ({ ...p, birthday: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Anniversary (optional)</label>
                    <input
                      type="date"
                      className={`${inputClass} mt-1`}
                      value={profile.anniversary}
                      onChange={(e) => setProfile((p) => ({ ...p, anniversary: e.target.value }))}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  disabled={savingProfile || !profileLoaded}
                  onClick={saveProfile}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingProfile ? "Saving…" : "Save profile"}
                </button>
              </div>
            </div>

            <ul className="mt-4 space-y-3">
              {docs.map((d) => {
                const data = d.data() || {};
                const inv =
                  Array.isArray(data.invoiceNumbers) && data.invoiceNumbers.length
                    ? data.invoiceNumbers.join(", ")
                    : data.invoiceNo?.toString() ?? "-";
                const amount = Number(data.amount) || 0;
                const pts = data.points != null ? Number(data.points) : calculatePoints(amount);
                const cr = data.createdAt?.toDate?.();
                const enteredBy = formatCustomerEntryActorLabel(
                  data.enteredBy?.toString() || data.createdByEmail?.toString() || data.createdByUid?.toString()
                );
                const isVoided = data.voided === true;
                return (
                  <li
                    key={d.id}
                    className={`rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm transition hover:shadow-md ${
                      isVoided ? "opacity-70 ring-1 ring-rose-100" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-gray-900">Invoice: {inv}</span>
                      <div className="flex shrink-0 items-center gap-2">
                        {isVoided && (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-800">
                            Void
                          </span>
                        )}
                        <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-bold text-indigo-700">
                          {Math.round(pts)} pts
                        </span>
                      </div>
                    </div>
                    <p className="mt-0.5 text-base font-semibold text-emerald-700">₹{Math.round(amount)}</p>
                    {cr && (
                      <p className="mt-2 text-xs text-gray-500">
                        {cr.toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    )}
                    {enteredBy && <p className="mt-1 text-xs text-gray-500">Entered by: {enteredBy}</p>}
                    {isVoided && data.voidReason && (
                      <p className="mt-2 text-xs text-rose-700">Void reason: {String(data.voidReason)}</p>
                    )}
                    {data.messageSent && (
                      <p className="mt-2 text-xs font-medium text-emerald-600">Message sent</p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canEdit && !isVoided && (
                        <button
                          type="button"
                          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-100"
                          onClick={() => openEdit(d)}
                        >
                          Edit
                        </button>
                      )}
                      {isAdmin && !isVoided && (
                        <button
                          type="button"
                          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-900 hover:bg-rose-100"
                          onClick={() => {
                            setVoidReason("");
                            setVoidTarget(d.id);
                          }}
                        >
                          Void
                        </button>
                      )}
                      {!isVoided && (
                        <button
                          type="button"
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
                          onClick={() => sendSms(d)}
                        >
                          Send SMS
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </main>

      {editOpen && editDoc && (
        <EditEntryModal
          docSnap={editDoc}
          onClose={() => {
            setEditOpen(false);
            setEditDoc(null);
          }}
        />
      )}

      {voidTarget && (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Void entry</h3>
            <p className="mt-1 text-sm text-gray-500">This marks the entry as void. A reason is required.</p>
            <textarea
              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={3}
              placeholder="Reason (required)"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setVoidTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                onClick={async () => {
                  if (!voidReason.trim()) {
                    toast.error("Enter a reason");
                    return;
                  }
                  const auth = getAuth();
                  try {
                    await updateDoc(doc(db, "customer_entries", voidTarget), {
                      voided: true,
                      voidReason: voidReason.trim(),
                      voidedAt: serverTimestamp(),
                      voidedByUid: auth.currentUser?.uid ?? null,
                      voidedByEmail: auth.currentUser?.email ?? null,
                    });
                    await writeAuditLog({
                      entity: "customer_entry",
                      entityId: voidTarget,
                      action: "void",
                      extra: { customerName, reason: voidReason.trim() },
                      uid: auth.currentUser?.uid ?? null,
                      email: auth.currentUser?.email ?? null,
                      displayName: auth.currentUser?.displayName ?? null,
                      role: (() => {
                        try {
                          return JSON.parse(localStorage.getItem("user") || "{}")?.role || "unknown";
                        } catch {
                          return "unknown";
                        }
                      })(),
                    });
                    toast.success("Entry voided");
                    setVoidTarget(null);
                  } catch (e) {
                    toast.error(String(e?.message || e));
                  }
                }}
              >
                Confirm void
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditEntryModal({ docSnap, onClose }) {
  const auth = getAuth();
  const data = docSnap.data() || {};
  const pairsIn = Array.isArray(data.invoiceAmountPairs) && data.invoiceAmountPairs.length
    ? data.invoiceAmountPairs.map((p) => ({
        invoice: String(p?.invoice ?? p?.invoiceNumber ?? ""),
        amount: p?.amount != null ? String(p.amount) : "",
      }))
    : [{ invoice: data.invoiceNo?.toString() ?? "", amount: String(data.amount ?? "") }];

  const [name, setName] = useState(data.customerName?.toString() ?? "");
  const [phone, setPhone] = useState(data.phone?.toString() ?? "");
  const [pairs, setPairs] = useState(pairsIn.length ? pairsIn : [{ invoice: "", amount: "" }]);
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const invoiceAmountPairs = [];
    const invoiceNumbers = [];
    let totalAmount = 0;
    for (const p of pairs) {
      const inv = p.invoice.trim();
      const amt = parseFloat(String(p.amount).replace(/,/g, "")) || 0;
      if (inv && amt > 0) {
        invoiceAmountPairs.push({ invoice: inv, amount: amt });
        totalAmount += amt;
        invoiceNumbers.push(inv);
      }
    }
    if (!name.trim()) {
      toast.error("Customer name required");
      return;
    }
    if (!invoiceAmountPairs.length) {
      toast.error("At least one invoice with amount");
      return;
    }

    for (const inv of invoiceNumbers) {
      const ex = await getDocs(
        query(collection(db, "customer_entries"), where("invoiceNumbers", "array-contains", inv))
      );
      const dup = ex.docs.find((x) => x.id !== docSnap.id);
      if (dup) {
        const other = dup.data()?.customerName?.toString()?.trim() || "another customer";
        toast.error(`Invoice "${inv}" is already used for "${other}".`);
        return;
      }
    }

    const totalPoints = calculatePoints(totalAmount);
    const invoiceDisplay = invoiceNumbers.length === 1 ? invoiceNumbers[0] : invoiceNumbers.join(", ");

    let updatedBy = await resolveCustomerEntryEnteredBy(auth, db);

    setSaving(true);
    try {
      await updateDoc(doc(db, "customer_entries", docSnap.id), {
        customerName: name.trim(),
        phone: phone.trim(),
        invoiceNo: invoiceDisplay,
        invoiceNumbers,
        invoiceAmountPairs,
        amount: totalAmount,
        points: totalPoints,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid ?? null,
        updatedByEmail: auth.currentUser?.email ?? null,
        updatedBy,
        ...(editNote.trim() ? { editNote: editNote.trim() } : {}),
      });
      try {
        const role = (() => {
          try {
            return JSON.parse(localStorage.getItem("user") || "{}")?.role;
          } catch {
            return "unknown";
          }
        })();
        await writeAuditLog({
          entity: "customer_entry",
          entityId: docSnap.id,
          action: "edit",
          extra: {
            customerName: name.trim(),
            amount: totalAmount,
            note: editNote.trim() || undefined,
          },
          uid: auth.currentUser?.uid ?? null,
          email: auth.currentUser?.email ?? null,
          displayName: auth.currentUser?.displayName ?? null,
          role: role || "unknown",
        });
      } catch (e) {
        console.warn(e);
      }
      toast.success("Entry updated");
      onClose();
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900">Edit entry</h3>
        <p className="mt-1 text-sm text-gray-500">Update customer, phone, and invoice lines.</p>
        <div className="mt-5 space-y-3 text-sm">
          <div>
            <label className="text-xs font-medium text-gray-600">Customer name</label>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Phone</label>
            <input className={inputClass} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          {pairs.map((row, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                className={inputClass}
                placeholder="Invoice"
                value={row.invoice}
                onChange={(e) => {
                  const n = [...pairs];
                  n[idx] = { ...n[idx], invoice: e.target.value };
                  setPairs(n);
                }}
              />
              <input
                className={`${inputClass} w-28`}
                placeholder="Amt"
                value={row.amount}
                onChange={(e) => {
                  const n = [...pairs];
                  n[idx] = { ...n[idx], amount: e.target.value };
                  setPairs(n);
                }}
              />
              {pairs.length > 1 && (
                <button
                  type="button"
                  className="mt-2 text-red-500 hover:text-red-700"
                  onClick={() => setPairs((p) => p.filter((_, i) => i !== idx))}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            onClick={() => setPairs((p) => [...p, { invoice: "", amount: "" }])}
          >
            + Add invoice
          </button>
          <div>
            <label className="text-xs font-medium text-gray-600">Change note (optional, for audit)</label>
            <textarea
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={2}
              placeholder="Why this change…"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
